import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useMemo } from 'react'
import { Controller, useFieldArray, useFormContext, useFormState, useWatch } from 'react-hook-form'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Alert, Grid, Stack } from '@mui/material'
import { Theme } from '@mui/material/styles'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

import Accordion from '@/components/ui/accordions/Accordion'
import InfoSection from '@/components/ui/sections/InfoSection'
import TextInput from '@/components/ui/inputs/TextInput'
import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { calculateAge, getGenderOptions } from '@/utils/patient'
import { toUtcMidnight } from '@/utils/dateUtils'

import { PatientProfileFormData } from '../model/usePatientProfileForm'
import { PatientIdentityStatus } from '@/core/models/patient.model'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { IDENTITY_NAME_MAX_LENGTH, sanitizeIdentityNameInput } from '../lib/identity-naming'
import { useInsBirthGivenPrefixValidation } from '../model/useInsBirthGivenPrefixValidation'
import { useResetInsOnStrictTraitChange } from '../model/useResetInsOnStrictTraitChange'

dayjs.extend(utc)

export function InsInfoSection({
  editable,
  isReferenceIdentityLocked = false,
  mode,
}: {
  editable: boolean
  identityStatus?: PatientIdentityStatus | null
  isReferenceIdentityLocked?: boolean
  mode?: 'create' | 'edit'
}) {
  const { i18n } = useLingui()
  const isMobile = useIsMobile()
  const { isINSEnabled: isInsEnabled } = useIsINSEnabled()
  const rhForm = useFormContext<PatientProfileFormData>()
  const [birthName, birthFirstName, birthSurname, insNIR, insOID, birthday, gender, birthPlaceCode, insEmitter] = useWatch({
    control: rhForm.control,
    name: ['birthName', 'birthFirstName', 'birthSurname', 'insNIR', 'insOID', 'birthday', 'gender', 'birthPlaceCode', 'insEmitter'],
  })
  const { dirtyFields } = useFormState({ control: rhForm.control })
  const { fields: insHistoryFields } = useFieldArray({ control: rhForm.control, name: 'insHistory' })

  const referenceFieldsReadOnly = isReferenceIdentityLocked || !editable
  const strictTraitsDirty = Boolean(
    dirtyFields.birthName || dirtyFields.birthSurname || dirtyFields.birthday || dirtyFields.gender || dirtyFields.birthPlaceCode
  )

  const lockedFieldSx = isReferenceIdentityLocked ? { '& .MuiOutlinedInput-root': { backgroundColor: (theme: Theme) => theme.palette.grey[100] } } : undefined
  const lockedLabel = (label: string) =>
    isReferenceIdentityLocked ? (
      <Stack component="span" direction="row" alignItems="center" spacing={0.5} sx={{ display: 'inline-flex' }}>
        {label}
        <LockOutlinedIcon aria-label={t`Non-modifiable field`} sx={{ fontSize: 15, color: 'text.disabled' }} />
      </Stack>
    ) : (
      label
    )

  useResetInsOnStrictTraitChange({
    mode,
    strictTraitsDirty,
    insNIR,
    insOID,
    setValue: rhForm.setValue,
  })

  useInsBirthGivenPrefixValidation({
    birthName,
    birthFirstName,
    editable,
    isInsEnabled,
    form: rhForm,
  })

  const genderOptions = useMemo(() => getGenderOptions().map((option) => ({ ...option, label: i18n._(option.label) })), [i18n, i18n.locale])

  const supplementaryAttributeOptions = useMemo(
    () => [
      { label: t`None`, value: '' },
      { label: t`Doubtful identity`, value: 'DOUTEUSE' },
      { label: t`Fictitious identity`, value: 'FICTIVE' },
    ],
    [i18n, i18n.locale]
  )

  return (
    <InfoSection label={t`INS information`}>
      <Grid container spacing={3} sx={{ mt: '0px' }}>
        {isReferenceIdentityLocked && (
          <Grid item xs={12}>
            <Alert severity="info">
              {t`The following fields are not editable for a recovered or qualified identity: INS registration number, OID, Last name at birth, Birth given name(s), Date of birth, Sex, and Birth location code (INSEE code).`}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextInputController
            control={rhForm.control}
            fullWidth
            label={lockedLabel(t`Last name at birth`)}
            name="birthSurname"
            readOnly={referenceFieldsReadOnly}
            sx={lockedFieldSx}
            transformValue={sanitizeIdentityNameInput}
            inputProps={{ maxLength: IDENTITY_NAME_MAX_LENGTH }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController
            control={rhForm.control}
            fullWidth
            label={lockedLabel(t`Birth given name(s)`)}
            name="birthName"
            readOnly={referenceFieldsReadOnly}
            sx={lockedFieldSx}
            transformValue={sanitizeIdentityNameInput}
            inputProps={{ maxLength: IDENTITY_NAME_MAX_LENGTH }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController control={rhForm.control} fullWidth label={t`Used last name`} name="surname" readOnly={!editable} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController control={rhForm.control} fullWidth label={t`Used first name`} name="name" readOnly={!editable} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController
            control={rhForm.control}
            fullWidth
            label={t`First birth given name`}
            name="birthFirstName"
            readOnly={!editable}
            transformValue={sanitizeIdentityNameInput}
            inputProps={{ maxLength: IDENTITY_NAME_MAX_LENGTH }}
          />
        </Grid>
        <Grid item xs={isMobile ? 7 : 6}>
          <Controller
            control={rhForm.control}
            name="birthday"
            render={({ field, fieldState }) => (
              <DatePickerInput
                disableFuture
                fullWidth
                name={field.name}
                label={lockedLabel(t`Birthday`)}
                onChange={(date) => {
                  rhForm.setValue('birthday', toUtcMidnight(date)?.toDate() ?? null)
                }}
                readOnly={referenceFieldsReadOnly}
                sx={lockedFieldSx}
                value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={isMobile ? 5 : 6}>
          <SelectInputController
            control={rhForm.control}
            id="gender"
            fullWidth
            label={lockedLabel(t`Sex`)}
            name="gender"
            options={genderOptions}
            readOnly={referenceFieldsReadOnly}
            sx={lockedFieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController
            control={rhForm.control}
            fullWidth
            label={lockedLabel(t`Birth location code (INSEE code)`)}
            name="birthPlaceCode"
            readOnly={referenceFieldsReadOnly}
            sx={lockedFieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            control={rhForm.control}
            name="birthday"
            render={({ field }) => <TextInput fullWidth label={t`Age`} name="age" value={field.value ? calculateAge(field.value) : ''} readOnly />}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectInputController
            control={rhForm.control}
            fullWidth
            label={t`Supplementary attribute`}
            name="identitySupplementaryAttribute"
            options={supplementaryAttributeOptions}
            readOnly={!editable}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController
            control={rhForm.control}
            fullWidth
            label={lockedLabel(t`INS registration number`)}
            name="insNIR"
            readOnly={referenceFieldsReadOnly}
            sx={lockedFieldSx}
            inputProps={{ maxLength: 15 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController
            control={rhForm.control}
            fullWidth
            label={lockedLabel(t`OID`)}
            name="insOID"
            readOnly={referenceFieldsReadOnly}
            sx={lockedFieldSx}
          />
        </Grid>
        {insEmitter ? (
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`Issuer (identity source)`} name="insEmitter" readOnly />
          </Grid>
        ) : null}
        {insHistoryFields.length > 0 && (
          <Grid item xs={12}>
            <Accordion title={t`Ins History`}>
              <Stack spacing={2}>
                {insHistoryFields.map((field, index) => (
                  <Grid item xs={12} key={field.id}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextInput
                          fullWidth
                          label={t`INS registration number`}
                          name={`insHistory.${index}.value`}
                          value={field.value?.length === 15 ? field.value : `${field.value ?? ''}${field.key ?? ''}`}
                          readOnly
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInputController control={rhForm.control} fullWidth label={t`Validity start date`} name={`insHistory.${index}.startDate`} readOnly />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInputController control={rhForm.control} fullWidth label={t`Validity end date`} name={`insHistory.${index}.endDate`} readOnly />
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Stack>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </InfoSection>
  )
}
